import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Close, CloudUpload, Delete, Undo } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { stopWheelNudge } from '../../utils/numberInput';
import {
  ACCENT,
  ACCENT_TINT,
  BrandLocation,
  Loading,
  LocationChecks,
  LocationProblem,
  LocationTabs,
  ProblemsPanel,
  ResultPanel,
  messageOf,
  plural,
  problemsOf,
  teachesAt,
  useBrandLocations,
  useLocationData,
} from './shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  CREATE CLASS — at one location or several
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Works like the ordinary create form: tick where it should exist ("Create
 * at"), fill everything in once.
 *
 *   One location   → Create.
 *   Several        → Next: one tab per location, each a full copy of what was
 *                    entered, editable just for that location (price, trial
 *                    price, instructor, room…). Then Create.
 *
 * Instructors and rooms belong to one location, so a location's tab only
 * offers its own. Class type and session type stay the same everywhere.
 */

type Fields = {
  className: string;
  image: string;
  description: string;
  duration: string;
  price: string;
  payAtStudio: boolean;
  isTrialEnabled: boolean;
  trialPrice: string;
  instructor: string;
  seatLayout: string;
};
type Key = keyof Fields;

const EMPTY: Fields = {
  className: '', image: '', description: '', duration: '', price: '',
  payAtStudio: false, isTrialEnabled: false, trialPrice: '', instructor: '', seatLayout: '',
};

const FORM = '__form';
const btnSx = { bgcolor: ACCENT, '&:hover': { bgcolor: '#d45180' } };

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (message: string) => void;
  /** Any location of the brand; its locations are offered. */
  anchorStudioId?: string | null;
  /** Ticked when the dialog opens. */
  preselect?: string[];
}

const CreateClassAtLocationsDialog: React.FC<Props> = ({ open, onClose, onCreated, anchorStudioId, preselect = [] }) => {
  const { creatable, loading: locLoading, error: locError } = useBrandLocations(anchorStudioId);
  const data = useLocationData();
  const { load } = data;

  const [step, setStep] = useState<'form' | 'locations'>('form');
  const [selected, setSelected] = useState<string[]>([]);
  const [tab, setTab] = useState('');
  const [classTypes, setClassTypes] = useState<any[]>([]);
  const [classType, setClassType] = useState('');
  const [sessionType, setSessionType] = useState<'group' | 'private' | 'duet'>('group');
  const [base, setBase] = useState<Fields>(EMPTY);
  const [over, setOver] = useState<Record<string, Partial<Fields>>>({});
  const [uploading, setUploading] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<LocationProblem[] | null>(null);
  const [result, setResult] = useState<{ created: any[]; failed: any[] } | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep('form'); setTab(''); setBase(EMPTY); setOver({}); setClassType(''); setSessionType('group');
    setError(null); setProblems(null); setResult(null); setSaving(false);
    apiService.getClassTypes().then((r: any) => setClassTypes(r.classTypes || [])).catch(() => setClassTypes([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const ok = preselect.filter((id) => creatable.some((l) => l._id === id));
    setSelected(ok.length ? ok : creatable.length === 1 ? [creatable[0]._id] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, creatable.length]);

  useEffect(() => { if (selected.length) load(selected); }, [selected, load]);

  const chosen: BrandLocation[] = creatable.filter((l) => selected.includes(l._id));
  const single = chosen.length === 1 ? chosen[0] : null;
  const anySessionTypes = chosen.some((l) => data.get(l._id)?.enableSessionTypes);
  const loadingData = data.isLoading(selected);

  /**
   * A location's copy: what was entered in the form, plus anything changed in
   * its own tab. The form's instructor and room carry over only where they
   * belong to that location.
   */
  const valueAt = (sid: string): Fields => {
    const o = over[sid] ?? {};
    const v: Fields = { ...base, ...o };
    const ld = data.get(sid);
    if (!('instructor' in o)) {
      const ins = ld?.instructors.find((i: any) => i._id === base.instructor);
      v.instructor = ins && teachesAt(ins, sid) ? base.instructor : '';
    }
    if (!('seatLayout' in o)) v.seatLayout = ld?.rooms.some((r: any) => r._id === base.seatLayout) ? base.seatLayout : '';
    return v;
  };
  const edited = (sid: string, k: Key) => k in (over[sid] ?? {});
  const setField = (sid: string, k: Key, value: any) => {
    if (sid === FORM) setBase((b) => ({ ...b, [k]: value }));
    else setOver((o) => ({ ...o, [sid]: { ...(o[sid] ?? {}), [k]: value } }));
    setProblems(null);
  };

  const issuesOf = (v: Fields, needInstructor: boolean): string[] => {
    const out: string[] = [];
    if (!v.className.trim()) out.push('name');
    if (!(Number(v.duration) > 0)) out.push('duration');
    if (v.price === '' || !(Number(v.price) >= 0)) out.push('price');
    if (v.isTrialEnabled && (v.trialPrice === '' || !(Number(v.trialPrice) >= 0))) out.push('trial price');
    if (needInstructor && !v.instructor) out.push('instructor');
    if (!v.image.trim()) out.push('image');
    if (!v.description.trim()) out.push('description');
    return out;
  };
  const formIssues = [
    ...(chosen.length ? [] : ['a location']),
    ...(classType ? [] : ['class type']),
    ...issuesOf(base, !!single),
  ];
  const issueCount = Object.fromEntries(chosen.map((l) => [l._id, issuesOf(valueAt(l._id), true).length]));
  const changedCount = Object.fromEntries(chosen.map((l) => [l._id, Object.keys(over[l._id] ?? {}).length]));
  const tabIssues = chosen.reduce((n, l) => n + issueCount[l._id], 0);

  // For the form with several locations ticked: every instructor across them.
  const unionInstructors = useMemo(() => {
    const map = new Map<string, { ins: any; at: string[] }>();
    chosen.forEach((l) => {
      (data.get(l._id)?.instructors ?? []).forEach((i: any) => {
        const e = map.get(i._id) ?? { ins: i, at: [] as string[] };
        e.at.push(l.name);
        map.set(i._id, e);
      });
    });
    return Array.from(map.values()).sort((a, b) => b.at.length - a.at.length || String(a.ins.name).localeCompare(b.ins.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen.map((l) => l._id).join(','), data]);

  const upload = async (sid: string, file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return setError(`"${file.name}" is not an image.`);
    if (file.size > 5 * 1024 * 1024) return setError('Images must be smaller than 5 MB.');
    setUploading(sid);
    setError(null);
    try {
      const res: any = await apiService.uploadFiles([file], 'classes');
      const url = res?.fileLinks?.[0];
      if (url) setField(sid, 'image', url);
    } catch (e: any) {
      setError(messageOf(e, 'The image could not be uploaded.'));
    } finally {
      setUploading(null);
    }
  };

  const submit = async () => {
    setSaving(true); setError(null); setProblems(null);
    const items = chosen.map((l) => {
      const v = valueAt(l._id);
      return {
        studioId: l._id,
        class: {
          className: v.className.trim(),
          image: v.image.trim(),
          description: v.description.trim(),
          duration: parseInt(v.duration, 10),
          instructor: v.instructor,
          classType,
          price: parseFloat(v.price),
          payAtStudio: v.payAtStudio,
          isTrialEnabled: v.isTrialEnabled,
          trialPrice: v.isTrialEnabled ? parseFloat(v.trialPrice) || 0 : 0,
          ...(data.get(l._id)?.enableSessionTypes ? { sessionType } : {}),
          ...(v.seatLayout ? { seatLayout: v.seatLayout } : {}),
        },
      };
    });
    try {
      const out: any = await apiService.createClassesAtLocations(items);
      setResult(out);
      if (!out.failed?.length) onCreated(`"${items[0].class.className}" created at ${plural(out.created.length, 'location')}`);
    } catch (e: any) {
      const p = problemsOf(e);
      if (p) {
        setProblems(p);
        if (chosen.length > 1) { setStep('locations'); setTab(p[0].studioId); }
      } else setError(messageOf(e, 'The class could not be created.'));
    } finally {
      setSaving(false);
    }
  };

  const goToLocations = () => {
    setTab(chosen[0]._id);
    setStep('locations');
  };

  // ── the form (used for "fill in once" and for each location's tab) ─────────
  const renderForm = (sid: string) => {
    const isForm = sid === FORM;
    const v = isForm ? base : valueAt(sid);
    // Which location's instructors and rooms to offer.
    const own = isForm ? (single ? data.get(single._id) : null) : data.get(sid);
    if (!isForm && !own) return <Loading label="Loading this location's instructors and rooms…" />;
    const mark = (k: Key): Record<string, any> => (!isForm && edited(sid, k) ? { '& .MuiOutlinedInput-root': { bgcolor: ACCENT_TINT } } : {});
    const serverIssues = (problems ?? []).filter((p) => p.studioId === sid);

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {serverIssues.length > 0 && <Alert severity="error">{serverIssues.map((p, i) => <div key={i}>{p.message}</div>)}</Alert>}

        <TextField label="Class name *" size="small" fullWidth value={v.className} sx={mark('className')}
          onChange={(e) => setField(sid, 'className', e.target.value)} />

        <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
          <TextField label="Duration (mins) *" type="number" size="small" fullWidth value={v.duration} onWheel={stopWheelNudge}
            sx={mark('duration')} onChange={(e) => setField(sid, 'duration', e.target.value)} />
          <TextField label="Price (₹) *" type="number" size="small" fullWidth value={v.price} onWheel={stopWheelNudge}
            sx={mark('price')} onChange={(e) => setField(sid, 'price', e.target.value)} />
        </Box>

        <Box display="flex" gap={2} alignItems="center" flexDirection={{ xs: 'column', sm: 'row' }}>
          <FormControlLabel sx={{ flex: 1, m: 0 }}
            control={<Switch checked={v.isTrialEnabled} onChange={(e) => setField(sid, 'isTrialEnabled', e.target.checked)} />}
            label="Offer a trial price" />
          {v.isTrialEnabled && (
            <TextField label="Trial price (₹) *" type="number" size="small" sx={{ flex: 1, ...mark('trialPrice') }} value={v.trialPrice}
              onWheel={stopWheelNudge} onChange={(e) => setField(sid, 'trialPrice', e.target.value)} helperText="Use 0 for a free trial." />
          )}
        </Box>

        {/* Instructor */}
        {own ? (
          <FormControl fullWidth size="small" error={!v.instructor} sx={mark('instructor')}>
            <InputLabel>Instructor *</InputLabel>
            <Select value={own.instructors.some((i: any) => i._id === v.instructor) ? v.instructor : ''} label="Instructor *"
              onChange={(e) => setField(sid, 'instructor', e.target.value)}>
              {own.instructors.length === 0 && <MenuItem disabled value="">No instructors teach here yet</MenuItem>}
              {own.instructors.map((i: any) => <MenuItem key={i._id} value={i._id}>{i.name}</MenuItem>)}
            </Select>
            {!v.instructor && (
              <FormHelperText>
                {own.instructors.length === 0
                  ? 'Add an instructor to this location first (Instructors → Edit → Locations).'
                  : isForm ? 'Choose who teaches this class.' : 'The instructor you chose does not teach here. Choose someone who does.'}
              </FormHelperText>
            )}
          </FormControl>
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel>Instructor</InputLabel>
            <Select value={unionInstructors.some((u) => u.ins._id === v.instructor) ? v.instructor : ''} label="Instructor"
              onChange={(e) => setField(sid, 'instructor', e.target.value)}>
              <MenuItem value=""><em>Choose later for each location</em></MenuItem>
              {unionInstructors.map(({ ins, at }) => (
                <MenuItem key={ins._id} value={ins._id}>
                  <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
                    <span>{ins.name}</span>
                    <Typography variant="caption" color="text.secondary">
                      {at.length === chosen.length ? 'all locations' : at.join(', ')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Used at the locations where they teach. You can pick someone else for any location next.</FormHelperText>
          </FormControl>
        )}

        {/* Room — only when we know which location's rooms to show */}
        {own && own.rooms.length > 0 && (
          <FormControl fullWidth size="small" sx={mark('seatLayout')}>
            <InputLabel>Room</InputLabel>
            <Select value={own.rooms.some((r: any) => r._id === v.seatLayout) ? v.seatLayout : ''} label="Room"
              onChange={(e) => setField(sid, 'seatLayout', e.target.value)}>
              <MenuItem value=""><em>No room, book by headcount</em></MenuItem>
              {own.rooms.map((r: any) => <MenuItem key={r._id} value={r._id}>{r.name} · {r.bookableCount} spots</MenuItem>)}
            </Select>
          </FormControl>
        )}

        {/* Image */}
        <Box>
          <Typography variant="body2" fontWeight={600} mb={1}>Image *</Typography>
          {v.image ? (
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box component="img" src={v.image} alt="" sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1.5, border: '1px solid #eee', outline: !isForm && edited(sid, 'image') ? `2px solid ${ACCENT}` : 'none' }} />
              <Button size="small" startIcon={<Delete />} onClick={() => setField(sid, 'image', '')}>Change</Button>
            </Box>
          ) : (
            <Box display="flex" gap={1.5} flexDirection={{ xs: 'column', sm: 'row' }}>
              <Button variant="outlined" component="label" startIcon={uploading === sid ? <CircularProgress size={14} /> : <CloudUpload />}
                disabled={uploading === sid} sx={{ borderStyle: 'dashed', flexShrink: 0 }}>
                {uploading === sid ? 'Uploading…' : 'Upload image'}
                <input type="file" hidden accept="image/*" onChange={(e) => { upload(sid, e.target.files?.[0]); e.target.value = ''; }} />
              </Button>
              <TextField size="small" fullWidth placeholder="or paste an image link"
                onBlur={(e) => e.target.value.trim() && setField(sid, 'image', e.target.value.trim())} />
            </Box>
          )}
        </Box>

        <TextField label="Description *" size="small" fullWidth multiline minRows={3} value={v.description} sx={mark('description')}
          onChange={(e) => setField(sid, 'description', e.target.value)} />

        <FormControlLabel control={<Switch checked={v.payAtStudio} onChange={(e) => setField(sid, 'payAtStudio', e.target.checked)} />} label="Pay at studio" />
      </Box>
    );
  };

  const current = chosen.find((l) => l._id === tab);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" fontWeight={700}>
          {step === 'form' ? 'Create Class' : `Check each location`}
        </Typography>
        {step === 'locations' && (
          <Typography variant="body2" color="text.secondary">
            Each location gets its own copy of what you entered. Change anything here just for that location.
          </Typography>
        )}
        <IconButton onClick={onClose} disabled={saving} sx={{ position: 'absolute', right: 12, top: 12 }} aria-label="Close"><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 420 }}>
        {result ? (
          <ResultPanel created={result.created.map((c: any) => ({ location: c.location, label: `"${c.className}" created` }))} failed={result.failed} />
        ) : step === 'form' ? (
          locLoading ? <Loading label="Loading your locations…" /> :
          locError ? <Alert severity="error">{locError}</Alert> :
          creatable.length === 0 ? <Alert severity="warning">You need manager access at a location to create classes there.</Alert> : (
            <Box display="flex" flexDirection="column" gap={2}>
              {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
              {problems && chosen.length === 1 && <ProblemsPanel problems={problems} />}
              <LocationChecks locations={creatable} selected={selected} onChange={(ids) => { setSelected(ids); setProblems(null); }} />
              <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                <FormControl fullWidth size="small" error={!classType}>
                  <InputLabel>Class type *</InputLabel>
                  <Select value={classType} label="Class type *" onChange={(e) => setClassType(e.target.value)}>
                    {classTypes.map((ct: any) => (
                      <MenuItem key={ct._id} value={ct._id}>
                        {ct.title}
                        {ct.studioType === 'wellness' && <Chip label="Wellness" size="small" sx={{ ml: 1, height: 18, fontSize: 11 }} />}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {anySessionTypes && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Session type</InputLabel>
                    <Select value={sessionType} label="Session type" onChange={(e) => setSessionType(e.target.value as any)}>
                      <MenuItem value="group">Group</MenuItem>
                      <MenuItem value="private">Private</MenuItem>
                      <MenuItem value="duet">Duet</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
              {loadingData && chosen.length > 0 ? <Loading label="Loading instructors…" /> : renderForm(FORM)}
            </Box>
          )
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {problems && <ProblemsPanel problems={problems} onGo={setTab} />}
            <LocationTabs value={tab} onChange={setTab} locations={chosen} issues={issueCount} customised={changedCount} withAllTab={false} />
            {current && changedCount[current._id] > 0 && (
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} px={1.5} py={1} borderRadius={2} bgcolor={ACCENT_TINT} gap={1} flexWrap="wrap">
                <Typography variant="body2">
                  {plural(changedCount[current._id], 'change')} just for {current.name} (highlighted).
                </Typography>
                <Button size="small" startIcon={<Undo />} sx={{ color: ACCENT, textTransform: 'none' }}
                  onClick={() => setOver((o) => ({ ...o, [current._id]: {} }))}>
                  Undo changes
                </Button>
              </Box>
            )}
            {current && renderForm(current._id)}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {result ? (
          <Button variant="contained" onClick={onClose} sx={btnSx}>Done</Button>
        ) : step === 'form' ? (
          <>
            <Typography variant="body2" color="text.secondary" flex={1}>
              {formIssues.length ? `Still needed: ${formIssues.join(', ')}` : chosen.length > 1 ? `${plural(chosen.length, 'copy', 'copies')} will be created` : ''}
            </Typography>
            <Button onClick={onClose} disabled={saving}>Cancel</Button>
            {chosen.length > 1 ? (
              <Button variant="contained" onClick={goToLocations} disabled={formIssues.length > 0 || loadingData} sx={btnSx}>
                Next: check {chosen.length} locations
              </Button>
            ) : (
              <Button variant="contained" onClick={submit} disabled={saving || formIssues.length > 0 || loadingData}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined} sx={btnSx}>
                {saving ? 'Creating…' : 'Create'}
              </Button>
            )}
          </>
        ) : (
          <>
            <Typography variant="body2" color={tabIssues ? 'error' : 'text.secondary'} flex={1}>
              {tabIssues ? 'Fix the locations marked in red' : `Ready to create at ${plural(chosen.length, 'location')}`}
            </Typography>
            <Button startIcon={<ArrowBack />} onClick={() => setStep('form')} disabled={saving}>Back</Button>
            <Button variant="contained" onClick={submit} disabled={saving || tabIssues > 0}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined} sx={btnSx}>
              {saving ? `Creating at ${plural(chosen.length, 'location')}…` : `Create at ${plural(chosen.length, 'location')}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateClassAtLocationsDialog;
